import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageBanner from "@/components/PageBanner";
import { Mail, MapPin, Globe, Linkedin, Phone, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import emailjs from '@emailjs/browser';
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import countriesData from "world-countries";

// Préparation de la liste des pays (Hors Israël)
const countries = countriesData
  .filter((c) => c.cca2 !== "IL")
  .map((c) => ({
    label: c.name.common,
    value: c.name.common.toLowerCase(),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "", company: "", country: "", email: "", 
    phone: "", product: "", volume: "", message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const serviceID = "service_260hfyc";
    const templateID = "template_6sopsne";
    const publicKey = "AY_dsdkXveNBkXeNu";

    emailjs.send(serviceID, templateID, formData, publicKey)
      .then(() => {
        toast.success("Thank you! Your inquiry has been sent.");
        setFormData({ 
          fullName: "", company: "", country: "", email: "", 
          phone: "", product: "", volume: "", message: "" 
        });
      })
      .catch((err) => {
        toast.error("Failed to send message. Please try again later.");
        console.error('EmailJS Error:', err);
      });
  };

  return (
    <div>
      <PageBanner
        title="Contact MM2A Tunisian Export"
        subtitle="We welcome serious business inquiries from professional buyers looking for Tunisian olive oil sourcing, blending and export cooperation. We reply within 24 hours."
      />

      <section className="section-padding">
        <div className="container-max grid md:grid-cols-3 gap-12">
          
          {/* Company Info Sidebar */}
          <div className="md:col-span-1">
            <h3 className="font-heading text-2xl font-bold text-foreground mb-6">Company Information</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-olive mt-1 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Location</p>
                  <p className="text-muted-foreground text-sm">Sfax, Tunisia</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-olive mt-1 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Commercial Email</p>
                  <a href="mailto:contact@mm2a.net" className="text-olive text-sm hover:underline">contact@mm2a.net</a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-olive mt-1 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Commercial Phone</p>
                  <a href="https://wa.me/21626677428" target="_blank" rel="noopener noreferrer" className="text-olive text-sm hover:underline">+216 26 677 428</a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Linkedin className="w-5 h-5 text-olive mt-1 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">LinkedIn</p>
                  <a href="https://www.linkedin.com/company/mm2a-tunisian-export/" target="_blank" rel="noopener noreferrer" className="text-olive text-sm hover:underline">MM2A Tunisian Export</a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl font-bold text-foreground mb-6">Send Us Your Inquiry</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Name & Company */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Full Name *</label>
                  <Input required value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Company Name *</label>
                  <Input required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
                </div>
              </div>

              {/* Country & Email */}
              <div className="grid md:grid-cols-2 gap-5">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Country *</label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
                        {formData.country ? countries.find((c) => c.value === formData.country)?.label : "Search country..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput placeholder="Search country..." />
                        <CommandEmpty>No country found.</CommandEmpty>
                        <CommandGroup className="max-h-60 overflow-y-auto">
                          {countries.map((country) => (
                            <CommandItem
                              key={country.value}
                              value={country.value}
                              onSelect={(v) => {
                                setFormData({ ...formData, country: v });
                                setOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", formData.country === country.value ? "opacity-100" : "opacity-0")} />
                              {country.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Email Address *</label>
                  <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
              </div>

              {/* Phone & Product */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Phone / WhatsApp *</label>
                 <PhoneInput
                  country={""} 
                  placeholder="Select your phone number"
                  excludeCountries={["il"]}
                  value={formData.phone}
                  onChange={(phone) => setFormData({ ...formData, phone })}
                  containerClass="!w-full"
                  inputClass="!w-full !h-10 !bg-background !border-input !rounded-md text-foreground"
                  buttonClass="!bg-background !border-input !rounded-l-md"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-foreground">Product Interest</label>
                  <Select value={formData.product} onValueChange={(v) => setFormData({...formData, product: v})}>
                    <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chemlali">Chemlali</SelectItem>
                      <SelectItem value="chetoui">Chetoui</SelectItem>
                      <SelectItem value="custom-blend">Custom Blend</SelectItem>
                      <SelectItem value="lampante">Lampante</SelectItem>
                      <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block text-foreground">Volume Requirement</label>
                <Input value={formData.volume} onChange={(e) => setFormData({...formData, volume: e.target.value})} />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block text-foreground">Message *</label>
                <Textarea required rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
              </div>

              <Button type="submit" size="lg" className="bg-primary hover:bg-olive-dark text-white font-semibold px-10">
                Send Inquiry
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;